import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { lineReveal, fadeUp } from "./content";

const HEADLINE = ["Locum clinical", "cover your", "surgery can", "trust."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-sage" />
          <span className="overline text-ink-soft">
            Advanced Nurse Practitioner &middot; UK GP Surgeries
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-end">
          {/* Headline */}
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <h1 className="font-display text-forest tracking-tight leading-[0.95] text-[3.2rem] sm:text-7xl xl:text-[5.6rem]">
              {HEADLINE.map((line, i) => (
                <span className="reveal-line" key={line}>
                  <motion.span
                    className="block"
                    variants={lineReveal}
                    custom={i}
                    initial="hidden"
                    animate="show"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              variants={fadeUp}
              custom={5}
              initial="hidden"
              animate="show"
              className="mt-8 max-w-xl text-lg leading-relaxed text-ink-soft"
            >
              Wellknowe Ltd is a Locum ANP providing GP locum and primary care locum
              cover to surgeries across the UK — face-to-face clinics, remote triage and
              remote workflow admin — so your team keeps caring for patients.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={6}
              initial="hidden"
              animate="show"
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contact"
                className="inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-base font-medium text-bone hover:bg-forest-accent transition-colors duration-200 group"
                data-testid="hero-cta-primary"
              >
                Enquire about cover
                <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#services"
                className="inline-flex items-center gap-2 rounded-full border border-sage px-8 py-4 text-base font-medium text-forest hover:bg-surface-alt transition-colors duration-200"
                data-testid="hero-cta-secondary"
              >
                Explore services
              </a>
            </motion.div>
          </motion.div>

          {/* Art-directed image */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[6px] border border-surface-alt bg-surface-alt aspect-[3/4]"
              data-testid="hero-image"
            >
              <motion.img
                src="https://static.prod-images.emergentagent.com/jobs/cde560a8-1811-4ac4-ade7-768fd959f3fd/images/bd8fec9e6ad76d645a7fcff0273b5f47ce7c181c312e387735169ae19701b538.png"
                alt="Standard NHS GP consulting room ready for locum clinical cover"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono-label text-[0.68rem] uppercase tracking-[0.2em] text-bone/90">
                  Face to face &middot; Remote
                </span>
                <span className="h-2 w-2 rounded-full bg-sage-light animate-pulse" />
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          variants={fadeUp}
          custom={8}
          initial="hidden"
          animate="show"
          className="mt-16 flex items-center gap-3 text-ink-soft"
        >
          <ArrowDown className="h-4 w-4 animate-bounce" />
          <span className="font-mono-label text-[0.7rem] uppercase tracking-[0.2em]">
            Scroll to see the services
          </span>
        </motion.div>
      </div>
    </section>
  );
}
